// Web front end. All server access goes through the shared core client, so the Android
// (Capacitor) and macOS (Tauri) shells can reuse this file and the core unchanged.
// Untrusted text (labels, manual titles, snippets, sources) is only ever set with textContent.

import {
  ManualiumClient, ApiError, type Item, type DocumentRecord, type ScoredCandidate, type SearchHit, type Capture, type StepCost,
} from '../../packages/core/src/index.ts';
import { getDocument, GlobalWorkerOptions, type PDFDocumentProxy } from 'pdfjs-dist';

GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string) => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
};
const button = (text: string, onClick: () => Promise<unknown> | void, cls?: string) => {
  const b = el('button', cls, text);
  b.onclick = async () => {
    b.disabled = true;
    try {
      await onClick();
    } catch (e) {
      fail(e);
    } finally {
      b.disabled = false;
    }
  };
  return b;
};
const usd = (micro: number) => (micro === 0 ? '$0' : `$${(micro / 1e6).toFixed(micro < 10_000 ? 5 : 4)}`);

let client: ManualiumClient;
let current: (Item & { documents: DocumentRecord[] }) | null = null;
const ITEMS_PAGE = 20;
const RESULTS_PAGE = 10;
const REVIEW_PAGE = 10;
let itemsOffset = 0;
let resultsOffset = 0;
let reviewOffset = 0;
let lastQuery = '';

function toast(msg: string) {
  const t = $('toast');
  t.textContent = msg;
  t.hidden = false;
  setTimeout(() => (t.hidden = true), 5000);
}

function fail(e: unknown) {
  if (e instanceof ApiError && e.status === 401) {
    sessionStorage.removeItem('manualium.devtoken');
    void showLogin();
  }
  toast(e instanceof Error ? e.message : String(e));
}

// --- sign-in -------------------------------------------------------------------------------------

const AUTH_ERRORS: Record<string, string> = {
  auth_not_configured: 'Google sign-in is not configured on this server yet.',
  auth_invalid: 'That sign-in expired or was already used. Try again.',
  auth_denied: 'Sign-in was cancelled.',
  auth_token_invalid: 'Google’s answer could not be verified. Try again.',
};

async function showLogin() {
  $('login').hidden = false;
  $('app').hidden = true;
  $('tabs').hidden = true;
  $('search-form').hidden = true;
  $('who').hidden = true;
  const info = await new ManualiumClient({ baseUrl: location.origin }).authInfo().catch(() => null);
  const problem = $('auth-problem');
  const hashErr = new URLSearchParams(location.hash.slice(1)).get('auth_error');
  if (info && !info.oidcConfigured) {
    problem.hidden = false;
    problem.textContent = `Google sign-in is not configured on this server. Missing: ${info.missing.join(', ')}. See docs/RUNBOOK.md → Google OAuth setup.`;
    ($('google-signin') as HTMLAnchorElement).classList.add('disabled');
  } else if (hashErr) {
    problem.hidden = false;
    problem.textContent = AUTH_ERRORS[hashErr] ?? `Sign-in failed (${hashErr}).`;
  }
  $('dev-form').hidden = !info?.devBypass;
}

function showSpend(me: Awaited<ReturnType<ManualiumClient['me']>>) {
  $('who-spend').textContent = me.llm.model
    ? `· model ${usd(me.llm.today.costMicroUsd)} of ${usd(me.llm.dailyBudgetMicroUsd)} today`
    : '· model off';
}

async function boot(devToken: string | null) {
  client = new ManualiumClient({ baseUrl: location.origin, token: devToken });
  let me;
  try {
    me = await client.me();
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return showLogin();
    return fail(e);
  }
  if (devToken) sessionStorage.setItem('manualium.devtoken', devToken);
  $('dev-banner').hidden = me.via !== 'dev-bypass';
  $('login').hidden = true;
  $('app').hidden = false;
  $('tabs').hidden = false;
  $('search-form').hidden = false;
  $('who').hidden = false;
  $('who-name').textContent = me.email ?? (me.via === 'dev-bypass' ? 'dev library' : 'signed in');
  showSpend(me);
  history.replaceState(null, '', location.pathname);
  await Promise.all([loadItems(0), loadReview(0)]);
}

// --- tabs ----------------------------------------------------------------------------------------

function showTab(name: 'capture' | 'library' | 'review') {
  for (const t of ['capture', 'library', 'review']) {
    $(`tab-${t}`).hidden = t !== name;
    document.querySelector(`[data-tab="${t}"]`)?.classList.toggle('active', t === name);
  }
  if (name === 'review') void loadReview(reviewOffset).catch(fail);
  if (name === 'library') void loadItems(itemsOffset).catch(fail);
}

// --- capture -------------------------------------------------------------------------------------

/** Keep uploads under the server's 5 MB image limit: re-encode big photos as JPEG, longest side 2400px. */
async function prepareImage(file: Blob): Promise<{ bytes: Blob; type: string }> {
  if (file.size <= 4 * 1024 * 1024) return { bytes: file, type: file.type || 'image/jpeg' };
  const bmp = await createImageBitmap(file);
  const scale = Math.min(1, 2400 / Math.max(bmp.width, bmp.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bmp.width * scale);
  canvas.height = Math.round(bmp.height * scale);
  canvas.getContext('2d')!.drawImage(bmp, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise<Blob>((r) => canvas.toBlob((b) => r(b!), 'image/jpeg', 0.9));
  return { bytes: blob, type: 'image/jpeg' };
}

async function sendPhoto(file: Blob) {
  $('capture-busy').hidden = false;
  $('result').hidden = true;
  try {
    const { bytes, type } = await prepareImage(file);
    const c = await client.capture(bytes, type);
    await renderCapture(c);
    await loadReview(0);
    showSpend(await client.me());
  } finally {
    $('capture-busy').hidden = true;
  }
}

let stream: MediaStream | null = null;

async function openCamera() {
  stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 } }, audio: false });
  const v = $('camera-video') as HTMLVideoElement;
  v.srcObject = stream;
  await v.play();
  $('camera').hidden = false;
}

function closeCamera() {
  stream?.getTracks().forEach((t) => t.stop());
  stream = null;
  $('camera').hidden = true;
}

async function snap() {
  const v = $('camera-video') as HTMLVideoElement;
  const canvas = document.createElement('canvas');
  canvas.width = v.videoWidth;
  canvas.height = v.videoHeight;
  canvas.getContext('2d')!.drawImage(v, 0, 0);
  const blob = await new Promise<Blob>((r) => canvas.toBlob((b) => r(b!), 'image/jpeg', 0.92));
  closeCamera();
  await sendPhoto(blob);
}

const OUTCOME: Record<string, [string, string]> = {
  auto_matched: ['Matched', 'matched'],
  ai_matched: ['Matched with the model’s help', 'likely'],
  needs_review: ['Needs your review', 'possible'],
};

function stepLine(name: string, s: StepCost | null, absent: string) {
  const li = el('li');
  if (!s) {
    li.append(el('span', 't', `${name}: `), document.createTextNode(absent));
    return li;
  }
  li.append(el('span', 't', `${name}: `), document.createTextNode(s.provider));
  li.append(el('span', `badge ${s.cached ? 'copy' : 'neutral'}`, s.cached ? 'cache hit' : 'computed'));
  li.append(el('span', 'muted', ` ${usd(s.costMicroUsd)}`));
  if (s.error) li.append(el('div', 'meta bad', s.error));
  return li;
}

async function photoUrl(captureId: string) {
  return URL.createObjectURL(await client.captureImage(captureId));
}

async function renderCapture(c: Capture) {
  $('result').hidden = false;
  ($('result-photo') as HTMLImageElement).src = await photoUrl(c.id);
  const id = c.identifiers;
  $('result-title').textContent = id.model ? `${id.make ?? 'Unknown make'} · ${id.model}` : 'No model number found';
  const [label, cls] = c.status === 'resolved' ? ['Resolved by you', 'matched'] : OUTCOME[c.decision.outcome] ?? ['?', 'possible'];
  const out = $('result-outcome');
  out.replaceChildren(el('span', `badge big ${cls}`, label));
  if (c.candidates[0]) out.append(el('span', 'muted', ` best score ${c.candidates[0].score.toFixed(2)} (${c.decision.band}) · paid ${usd(c.decision.paidMicroUsd)}`));
  const why = $('result-why');
  why.replaceChildren(...c.decision.why.map((w) => el('li', undefined, w)));
  if (c.decision.importError) why.append(el('li', 'bad', `Import failed: ${c.decision.importError}`));

  const actions = $('result-actions');
  actions.replaceChildren();
  if (c.documentId && c.itemId) {
    actions.append(button('Open manual', () => openViewer(c.documentId!, c.candidates[c.decision.chosen ?? 0]?.title ?? 'Manual', 1)));
    actions.append(button('Open item', async () => { showTab('library'); await openItem(c.itemId!); }, 'ghost'));
  } else if (c.status === 'needs_review') {
    actions.append(button('Go to review', () => showTab('review')));
  }

  const dl = $('result-ids');
  dl.replaceChildren();
  const add = (k: string, v: string | null) => dl.append(el('dt', undefined, k), el('dd', undefined, v || '—'));
  add('Make', id.make);
  add('Model', id.model);
  add('Serial', id.serial);
  add('GTIN / UPC', id.gtins.join(', '));
  add('URLs', id.urls.join(' '));
  add('Codes', c.codes.map((x) => `${x.format}: ${x.text}`).join(' · '));
  add('Evidence', id.evidence.join(' · '));

  const steps = $('result-steps');
  const d = c.decision.steps;
  steps.replaceChildren(
    stepLine('Barcodes/QR', d.barcode, ''),
    stepLine('OCR', d.ocr, ''),
    stepLine('Model', d.llm, 'not needed (score decided)'),
  );
  const src = el('li');
  src.append(el('span', 't', 'Sources: '), document.createTextNode(`${d.sources.fetched} queried, ${d.sources.cached} from cache${d.sources.errors.length ? ` · ${d.sources.errors.join('; ')}` : ''}`));
  steps.append(src);
  if (c.decision.model) {
    const m = c.decision.model;
    steps.append(el('li', 'meta', `Model said: ${m.choice === null ? 'none of these' : `#${m.choice + 1}`} at ${m.confidence.toFixed(2)} — “${m.reason}” (${m.modelId}, ${m.inputTokens}+${m.outputTokens} tokens${m.cached ? ', cached' : ''})`));
  } else if (c.decision.modelNote) {
    steps.append(el('li', 'meta', `Model: ${c.decision.modelNote}`));
  }

  const ul = $('result-candidates');
  ul.replaceChildren();
  if (!c.candidates.length) ul.append(el('li', 'muted', 'No candidate manuals.'));
  c.candidates.forEach((cand, i) => ul.append(candidateCard(cand, i === c.decision.chosen && c.status !== 'needs_review')));
}

const LABEL: Record<ScoredCandidate['label'], string> = { matched: 'Matched', likely: 'Likely', possible: 'Possible' };

function candidateCard(c: ScoredCandidate, chosen: boolean, action?: HTMLElement) {
  const li = el('li', chosen ? 'chosen' : undefined);
  const head = el('div');
  head.append(el('span', 't', c.title), el('span', `badge ${c.label}`, `${LABEL[c.label]} ${c.score.toFixed(2)}`));
  if (chosen) head.append(el('span', 'badge copy', 'chosen'));
  li.append(head);
  const where = c.connectorId === 'library' ? 'already in your library' : `${c.connectorId}${c.sourceUrl ? ` · ${new URL(c.sourceUrl).host}` : ''}`;
  li.append(el('div', 'meta', `${where}${c.make ? ` · ${c.make}` : ''}${c.model ? ` · model ${c.model}` : ''}`));
  const sig = Object.entries(c.signals).filter(([, v]) => v > 0).map(([k, v]) => (v === 1 ? k : `${k} ${v}`));
  li.append(el('div', 'meta', `Signals: ${sig.join(', ') || 'none'}${c.reasons.length ? ` · ${c.reasons.join(' · ')}` : ''}`));
  if (action) li.append(action);
  return li;
}

// --- review queue --------------------------------------------------------------------------------

async function loadReview(offset: number) {
  const page = await client.listCaptures('needs_review', offset, REVIEW_PAGE);
  reviewOffset = offset;
  $('review-count').textContent = page.total ? String(page.total) : '';
  $('review-total').textContent = `(${page.total})`;
  const ul = $('review-list');
  ul.replaceChildren();
  if (!page.items.length) ul.append(el('li', 'muted', 'Nothing waiting. Everything you captured was matched.'));
  for (const c of page.items) ul.append(await reviewCard(c));
  $('review-page').textContent = page.total ? `${offset + 1}–${offset + page.items.length} of ${page.total}` : '';
  ($('review-prev') as HTMLButtonElement).disabled = offset === 0;
  ($('review-next') as HTMLButtonElement).disabled = offset + page.items.length >= page.total;
}

async function afterResolve(c: Capture, msg: string) {
  toast(msg);
  await loadReview(reviewOffset);
  showTab('capture');
  await renderCapture(c);
}

async function reviewCard(c: Capture) {
  const li = el('li', 'review');
  const img = el('img', 'thumb');
  img.alt = 'Captured photo';
  img.src = await photoUrl(c.id);
  const body = el('div');
  const ids = c.identifiers;
  body.append(el('div', 't', ids.model ? `${ids.make ?? 'Unknown make'} · ${ids.model}` : 'No model number read'));
  body.append(el('div', 'meta', `${new Date(c.createdAt).toLocaleString()}${ids.serial ? ` · serial ${ids.serial}` : ''}${ids.gtins.length ? ` · GTIN ${ids.gtins.join(', ')}` : ''}`));
  const why = el('ul', 'why');
  why.append(...c.decision.why.map((w) => el('li', undefined, w)));
  body.append(why);

  const cands = el('ul', 'list');
  c.candidates.slice(0, 5).forEach((cand, i) => cands.append(candidateCard(cand, false,
    button('This one', async () => afterResolve(await client.resolvePick(c.id, i), 'Saved. We’ll match this label automatically next time.')))));
  if (c.candidates.length) body.append(el('h3', undefined, 'Pick the right manual'), cands);

  body.append(el('h3', undefined, 'Or tell us what it is'));
  const form = el('form', 'inline');
  const make = el('input');
  make.placeholder = 'Make';
  make.value = ids.make ?? '';
  make.required = true;
  const model = el('input');
  model.placeholder = 'Model';
  model.value = ids.model ?? '';
  model.required = true;
  const save = el('button', undefined, 'Save');
  save.type = 'submit';
  form.append(make, model, save);
  form.onsubmit = async (e) => {
    e.preventDefault();
    try {
      await afterResolve(await client.resolveManual(c.id, make.value, model.value), 'Saved. Find or upload its manual from the item.');
    } catch (err) {
      fail(err);
    }
  };
  body.append(form);

  const row = el('div', 'row');
  const up = el('label', 'file', 'Upload the manual (PDF) ');
  const file = el('input');
  file.type = 'file';
  file.accept = 'application/pdf,.pdf';
  file.onchange = async () => {
    const f = file.files?.[0];
    if (!f) return;
    try {
      await afterResolve(await client.resolveUpload(c.id, await f.arrayBuffer(), f.name, make.value || undefined, model.value || undefined), 'Uploaded, encrypted and remembered.');
    } catch (err) {
      fail(err);
    }
  };
  up.append(file);
  row.append(up, button('Dismiss', async () => { await client.dismiss(c.id); await loadReview(reviewOffset); }, 'ghost'));
  body.append(row);
  li.append(img, body);
  return li;
}

// --- library -------------------------------------------------------------------------------------

async function loadItems(offset: number) {
  const page = await client.listItems(offset, ITEMS_PAGE);
  itemsOffset = offset;
  const ul = $('items');
  ul.replaceChildren();
  for (const it of page.items) {
    const li = el('li', 'clickable');
    li.append(el('div', 't', it.nickname ?? `${it.make} ${it.modelRaw}`), el('div', 'meta', `${it.make} · ${it.modelRaw}${it.serial ? ` · S/N ${it.serial}` : ''}${it.room ? ` · ${it.room}` : ''}`));
    li.onclick = () => openItem(it.id).catch(fail);
    ul.append(li);
  }
  if (!page.items.length) ul.append(el('li', 'muted', 'Nothing yet. Capture a label or add an item.'));
  $('lib-count').textContent = `(${page.total})`;
  $('items-page').textContent = page.total ? `${offset + 1}–${offset + page.items.length} of ${page.total}` : '';
  ($('items-prev') as HTMLButtonElement).disabled = offset === 0;
  ($('items-next') as HTMLButtonElement).disabled = offset + page.items.length >= page.total;
}

async function openItem(id: string) {
  current = await client.getItem(id);
  $('results').hidden = true;
  $('detail').hidden = false;
  $('detail-title').textContent = current.nickname ?? `${current.make} ${current.modelRaw}`;
  $('detail-sub').textContent = `${current.make} · model ${current.modelRaw} (normalised ${current.modelNormalized})${current.serial ? ` · serial ${current.serial}` : ''}${current.gtins.length ? ` · GTIN ${current.gtins.join(', ')}` : ''}${current.room ? ` · ${current.room}` : ''}`;
  renderDocs();
  await runLookup();
}

function renderDocs() {
  const ul = $('docs');
  ul.replaceChildren();
  if (!current?.documents.length) ul.append(el('li', 'muted', 'No manuals yet.'));
  for (const d of current?.documents ?? []) {
    const li = el('li');
    const head = el('div');
    head.append(el('span', 't', d.title), el('span', `badge ${d.storage}`, d.storage === 'copy' ? 'Stored · encrypted' : 'Link only'));
    li.append(head);
    li.append(el('div', 'meta', d.storage === 'copy' ? `${d.pageCount} pages · ${(d.sizeBytes! / 1024).toFixed(0)} KB` : 'Not stored: open it at the source, or upload your own copy.'));
    const row = el('div', 'row');
    if (d.storage === 'copy') {
      row.append(button('Open', () => openViewer(d.id, d.title, 1)));
    } else if (d.sourceUrl) {
      const a = el('a', undefined, 'Open source');
      a.href = d.sourceUrl;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      row.append(a);
    }
    li.append(row, provenance(d));
    ul.append(li);
  }
}

/** "Where this came from" (spec: UX -> Provenance). */
function provenance(d: DocumentRecord) {
  const det = el('details');
  det.append(el('summary', undefined, 'Where this came from'));
  const dl = el('dl');
  const add = (k: string, v: string | null) => { dl.append(el('dt', undefined, k), el('dd', undefined, v ?? '—')); };
  add('Source', d.connectorId);
  add('URL', d.sourceUrl);
  add('Licence basis', d.licenceBasis);
  add('SHA-256', d.contentHash);
  add('Added', new Date(d.createdAt).toLocaleString());
  det.append(dl);
  return det;
}

async function runLookup() {
  if (!current) return;
  const status = $('lookup-status');
  const ul = $('candidates');
  ul.replaceChildren();
  status.textContent = 'Searching permitted sources…';
  try {
    const r = await client.lookup({ make: current.make, model: current.modelRaw }, 0, 10);
    const errs = r.errors.map((e) => `${e.connectorId} unavailable (${e.code})`).join('; ');
    status.textContent = `${r.items.length} of ${r.total} candidates${errs ? ` · ${errs}` : ''}`;
    for (const c of r.items) {
      ul.append(candidateCard(c, false, button('Add to library', async () => {
        const res = await client.attachCandidate(current!.id, c.connectorId, c.ref);
        toast(res.document.storage === 'copy' ? 'Manual stored (encrypted) and indexed.' : `Kept as a link: ${res.document.licenceBasis}`);
        await openItem(current!.id);
      })));
    }
  } catch (e) {
    status.textContent = 'Lookup failed.';
    fail(e);
  }
}

// --- search --------------------------------------------------------------------------------------

function snippet(text: string) {
  // The server marks hits with «»; rebuild as text nodes + <mark>, never as HTML.
  const out = el('div', 'meta');
  text.split(/(«[^»]*»)/).forEach((part) => {
    if (part.startsWith('«') && part.endsWith('»')) out.append(el('mark', undefined, part.slice(1, -1)));
    else if (part) out.append(document.createTextNode(part));
  });
  return out;
}

async function runSearch(q: string, offset: number) {
  lastQuery = q;
  const r = await client.search(q, offset, RESULTS_PAGE);
  resultsOffset = offset;
  showTab('library');
  $('results').hidden = false;
  $('detail').hidden = true;
  $('results-count').textContent = `(${r.total})`;
  const ul = $('results-list');
  ul.replaceChildren();
  if (!r.items.length) ul.append(el('li', 'muted', 'No matches.'));
  for (const h of r.items) ul.append(hitRow(h));
  $('results-page').textContent = r.total ? `${offset + 1}–${offset + r.items.length} of ${r.total}` : '';
  ($('results-prev') as HTMLButtonElement).disabled = offset === 0;
  ($('results-next') as HTMLButtonElement).disabled = offset + r.items.length >= r.total;
}

function hitRow(h: SearchHit) {
  const li = el('li', 'clickable');
  li.append(el('div', 't', h.title), el('div', 'meta', h.type === 'item' ? 'Item' : h.page ? `Manual · page ${h.page}` : 'Manual'));
  if (h.snippet) li.append(snippet(h.snippet));
  li.onclick = () => {
    const go = h.documentId && h.page ? openViewer(h.documentId, h.title, h.page) : openItem(h.itemId);
    go.catch(fail);
  };
  return li;
}

// --- viewer (pdf.js: renders to canvas, never runs a PDF's scripts) -------------------------------

let pdf: PDFDocumentProxy | null = null;
let pageNo = 1;

async function openViewer(documentId: string, title: string, page: number) {
  const bytes = await client.documentContent(documentId);
  await pdf?.destroy();
  pdf = await getDocument({ data: new Uint8Array(bytes), isEvalSupported: false, enableXfa: false }).promise;
  $('viewer').hidden = false;
  $('viewer-title').textContent = title;
  await renderPage(Math.min(Math.max(1, page), pdf.numPages));
  $('viewer').scrollIntoView({ behavior: 'smooth' });
}

async function renderPage(n: number) {
  if (!pdf) return;
  pageNo = n;
  const page = await pdf.getPage(n);
  const wrap = $('canvas-wrap');
  const base = page.getViewport({ scale: 1 });
  const scale = Math.min(2, (wrap.clientWidth || 800) / base.width) * (window.devicePixelRatio || 1);
  const vp = page.getViewport({ scale });
  const canvas = $('page-canvas') as HTMLCanvasElement;
  canvas.width = vp.width;
  canvas.height = vp.height;
  canvas.style.width = `${vp.width / (window.devicePixelRatio || 1)}px`;
  await page.render({ canvasContext: canvas.getContext('2d')!, viewport: vp }).promise;
  $('pg-label').textContent = `Page ${n} of ${pdf.numPages}`;
  ($('pg-prev') as HTMLButtonElement).disabled = n <= 1;
  ($('pg-next') as HTMLButtonElement).disabled = n >= pdf.numPages;
}

// --- wiring --------------------------------------------------------------------------------------

$('dev-form').addEventListener('submit', (e) => {
  e.preventDefault();
  void boot(($('token') as HTMLInputElement).value.trim());
});
$('google-signin').addEventListener('click', (e) => {
  if ((e.currentTarget as HTMLElement).classList.contains('disabled')) e.preventDefault();
});
$('logout').onclick = async () => {
  await client.logout().catch(() => {});
  sessionStorage.removeItem('manualium.devtoken');
  location.reload();
};
document.querySelectorAll<HTMLButtonElement>('[data-tab]').forEach((b) => {
  b.onclick = () => showTab(b.dataset.tab as 'capture' | 'library' | 'review');
});

$('photo').addEventListener('change', async (e) => {
  const input = e.target as HTMLInputElement;
  const f = input.files?.[0];
  input.value = '';
  if (f) await sendPhoto(f).catch(fail);
});
$('camera-open').onclick = () => openCamera().catch((e) => fail(new Error(`Camera unavailable: ${(e as Error).message}. Use “Upload or take a photo” instead.`)));
$('camera-close').onclick = closeCamera;
$('camera-snap').onclick = () => snap().catch(fail);

$('add-form').addEventListener('submit', async (e) => {
  e.preventDefault();
  const f = new FormData(e.target as HTMLFormElement);
  try {
    const it = await client.createItem({
      make: String(f.get('make')), model: String(f.get('model')),
      nickname: String(f.get('nickname') ?? '') || undefined, room: String(f.get('room') ?? '') || undefined,
    });
    (e.target as HTMLFormElement).reset();
    await loadItems(0);
    await openItem(it.id);
  } catch (err) {
    fail(err);
  }
});

$('upload').addEventListener('change', async (e) => {
  const input = e.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file || !current) return;
  try {
    const r = await client.attachUpload(current.id, await file.arrayBuffer(), file.name);
    toast(r.deduplicated ? 'Already in your library.' : 'Uploaded, encrypted and indexed.');
    await openItem(current.id);
  } catch (err) {
    fail(err);
  } finally {
    input.value = '';
  }
});

$('url-form').addEventListener('submit', async (e) => {
  e.preventDefault();
  const url = ($('url') as HTMLInputElement).value.trim();
  if (!url || !current) return;
  try {
    const r = await client.attachUrl(current.id, url);
    toast(r.document.storage === 'copy' ? 'Fetched, stored and indexed.' : `Kept as a link: ${r.document.licenceBasis}`);
    ($('url') as HTMLInputElement).value = '';
    await openItem(current.id);
  } catch (err) {
    fail(err);
  }
});

$('search-form').addEventListener('submit', (e) => {
  e.preventDefault();
  const q = ($('search-q') as HTMLInputElement).value.trim();
  if (q) runSearch(q, 0).catch(fail);
});

$('items-prev').onclick = () => loadItems(Math.max(0, itemsOffset - ITEMS_PAGE)).catch(fail);
$('items-next').onclick = () => loadItems(itemsOffset + ITEMS_PAGE).catch(fail);
$('results-prev').onclick = () => runSearch(lastQuery, Math.max(0, resultsOffset - RESULTS_PAGE)).catch(fail);
$('results-next').onclick = () => runSearch(lastQuery, resultsOffset + RESULTS_PAGE).catch(fail);
$('review-prev').onclick = () => loadReview(Math.max(0, reviewOffset - REVIEW_PAGE)).catch(fail);
$('review-next').onclick = () => loadReview(reviewOffset + REVIEW_PAGE).catch(fail);
$('pg-prev').onclick = () => renderPage(pageNo - 1).catch(fail);
$('pg-next').onclick = () => renderPage(pageNo + 1).catch(fail);
$('viewer-close').onclick = () => { $('viewer').hidden = true; };

// Session cookie first (Google sign-in); the dev token only if this tab used the bypass.
void boot(sessionStorage.getItem('manualium.devtoken'));
